import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Mic, Volume2, Rabbit, Bookmark, Sparkles, Loader2, Send } from "lucide-react";
import client, { formatApiError } from "@/lib/api";
import { playTTS } from "@/lib/audio";
import { useRecorder } from "@/hooks/useRecorder";
import PronounceModal from "@/components/PronounceModal";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";

export default function SayItForMe() {
  const nav = useNavigate();
  const rec = useRecorder();
  const { profile } = useAuth();
  const { t } = useLang();
  const [text, setText] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [practice, setPractice] = useState(false);
  const [swiss, setSwiss] = useState(null);
  const [swissLoading, setSwissLoading] = useState(false);

  const loadSwiss = async () => {
    if (swiss) { setSwiss(null); return; }
    setSwissLoading(true);
    try { const { data } = await client.post("/swiss-hint", { text: result.translation }); setSwiss(data); }
    catch { toast.error(t("couldnt_load_swiss")); } finally { setSwissLoading(false); }
  };

  const submit = async (value) => {
    const q = (value ?? text).trim();
    if (!q) return;
    setLoading(true); setResult(null);
    try { const { data } = await client.post("/say-it-for-me", { text: q }); setResult(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const toggleMic = async () => {
    if (recording) {
      const res = await rec.stop(); setRecording(false);
      if (!res || !res.blob) return;
      setLoading(true);
      try { const fd = new FormData(); fd.append("file", res.blob, res.filename);
        const { data } = await client.post("/stt", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const txt = (data.text || "").trim();
        if (!txt) { toast.error(t("didnt_catch")); setLoading(false); return; }
        setText(txt); submit(txt);
      } catch { toast.error(t("couldnt_hear")); setLoading(false); }
    } else { const ok = await rec.start(); if (ok) setRecording(true); else toast.error(t("mic_permission")); }
  };
  const save = async () => {
    try { await client.post("/phrases", { target_text: result.translation, translation: text, category: "Daily Life" }); toast.success(t("saved_to_phrases")); }
    catch { toast.error(t("couldnt_save")); }
  };

  return (
    <div className="min-h-screen w-full bg-[#04040c] flex justify-center">
      <div className="relative w-full max-w-[430px] min-h-screen app-bg flex flex-col px-5 py-6">
        <header className="flex items-center gap-3 mb-4">
          <button onClick={() => nav(-1)} data-testid="back-btn" className="w-10 h-10 rounded-full glass flex items-center justify-center text-slate-300 press"><ArrowLeft size={20} /></button>
          <div><h1 className="font-heading text-xl font-extrabold text-white">{t("say_it")}</h1><p className="text-sm text-slate-400">{t("say_sub")}</p></div>
        </header>

        <div className="glass rounded-2xl p-4">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} data-testid="say-input"
            placeholder={t("say_placeholder")}
            className="w-full resize-none outline-none bg-transparent text-white placeholder:text-slate-500" />
          <div className="flex items-center justify-between mt-2">
            <button onClick={toggleMic} data-testid="say-mic" className={`w-11 h-11 rounded-full flex items-center justify-center press ${recording ? "bg-red-500 text-white" : "bg-white/5 text-violet-300"}`}><Mic size={18} /></button>
            <button onClick={() => submit()} disabled={loading || !text.trim()} data-testid="say-submit" className="mel-gradient text-white font-semibold px-6 py-2.5 rounded-full flex items-center gap-2 disabled:opacity-50 press">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} {t("translate_btn")}
            </button>
          </div>
        </div>

        {loading && !result && <div className="mt-6 flex items-center justify-center gap-2 text-slate-400"><Loader2 className="animate-spin" size={18} /> {t("translating")}</div>}

        {result && (
          <div className="mt-6 animate-float-up" data-testid="say-result">
            <div className="glass-strong rounded-3xl p-5 shadow-[0_20px_50px_-20px_rgba(37,99,235,0.7)]">
              <span className="text-xs font-bold tracking-widest text-cyan-300">🇩🇪 {t("say_this_caps")}</span>
              <p className="font-heading text-2xl font-extrabold text-white mt-2 leading-snug">{result.translation}</p>
              {result.literal && <p className="text-slate-400 mt-2 text-sm">{result.literal}</p>}
              {result.pronunciation_tip && <p className="text-slate-300 mt-3 text-sm bg-white/5 rounded-xl p-3 flex gap-2"><Sparkles size={15} className="text-cyan-300 shrink-0 mt-0.5" /> {result.pronunciation_tip}</p>}
              <div className="grid grid-cols-3 gap-2 mt-5">
                <button onClick={() => playTTS(result.translation, 1.0)} data-testid="say-normal" className="flex flex-col items-center gap-1 bg-white/5 rounded-2xl py-3 text-violet-200 font-semibold text-sm press"><Volume2 size={20} /> {t("normal")}</button>
                <button onClick={() => playTTS(result.translation, 0.6)} data-testid="say-slow" className="flex flex-col items-center gap-1 bg-white/5 rounded-2xl py-3 text-violet-200 font-semibold text-sm press"><Rabbit size={20} /> {t("slow")}</button>
                <button onClick={save} data-testid="say-save" className="flex flex-col items-center gap-1 bg-white/5 rounded-2xl py-3 text-violet-200 font-semibold text-sm press"><Bookmark size={20} /> {t("save")}</button>
              </div>
              <button onClick={() => setPractice(true)} data-testid="say-practice" className="mt-3 w-full mel-gradient text-white font-semibold py-3 rounded-2xl flex items-center justify-center gap-2 press">🎙 {t("practice_pron")}</button>
              {profile?.learning_lang === "de" && (
                <button onClick={loadSwiss} data-testid="say-swiss" className="mt-2 w-full glass text-red-200 font-semibold py-3 rounded-2xl flex items-center justify-center gap-2 press">
                  {swissLoading ? <Loader2 size={16} className="animate-spin" /> : "🇨🇭"} {swiss ? t("hide_swiss") : t("swiss_german")}
                </button>
              )}
              {swiss && (
                <div className="mt-3 bg-red-500/10 border border-red-400/20 rounded-xl p-3" data-testid="say-swiss-result">
                  <p className="text-red-100 font-semibold">{swiss.swiss}</p>
                  {swiss.note && <p className="text-slate-400 text-sm mt-1">{swiss.note}</p>}
                  <button onClick={() => playTTS(swiss.swiss, 0.92)} data-testid="say-swiss-listen"
                    className="mt-2 flex items-center gap-1 text-xs text-red-300 font-semibold press">
                    <Volume2 size={13} /> {t("listen_swiss")}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        {practice && <PronounceModal phrase={result.translation} translation={text} onClose={() => setPractice(false)} />}
      </div>
    </div>
  );
}
